(() => {
  const films = new Map();
  const initializeFilm = (video) => {
    if (films.has(video)) return;
    const toggle = video.closest('[data-hm-r2-film-player]')?.querySelector('[data-hm-r2-film-toggle]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    let inViewport = false;
    let userPaused = false;
    let autoplayBlocked = false;
    let destroyed = false;
    let expectedPlaying = false;
    let internalPauses = 0;
    let playAttempt = null;
    let manualPlaybackRequest = 0;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    const updateToggle = () => {
      if (!toggle || destroyed) return;
      const paused = video.paused || video.ended;
      const label = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
      toggle.dataset.state = paused ? 'paused' : 'playing';
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('title', label);
    };
    const pauseForEnvironment = () => {
      if (playAttempt) playAttempt.cancelled = true;
      expectedPlaying = false;
      if (!video.paused) {
        internalPauses += 1;
        video.pause();
      }
    };
    const syncPlayback = () => {
      if (destroyed) return;
      if (video.paused && expectedPlaying && !playAttempt) {
        userPaused = true;
        expectedPlaying = false;
      }
      const allowMotion = !reducedMotion.matches && !connection?.saveData;
      video.loop = allowMotion;
      if (!allowMotion || !inViewport || document.visibilityState === 'hidden') {
        pauseForEnvironment();
        return;
      }
      if (userPaused || autoplayBlocked || playAttempt || !video.paused) return;
      const attempt = { cancelled: false };
      playAttempt = attempt;
      expectedPlaying = true;
      const settle = (error) => {
        if (destroyed || playAttempt !== attempt) return;
        playAttempt = null;
        if (error && (!attempt.cancelled || error.name !== 'AbortError')) {
          autoplayBlocked = true;
          expectedPlaying = false;
          updateToggle();
          return;
        }
        if (attempt.cancelled) syncPlayback();
      };
      try {
        const result = video.play();
        if (result?.then) result.then(() => settle(), settle);
        else settle();
      } catch (error) {
        settle(error);
      }
    };
    const onPause = () => {
      if (internalPauses > 0) internalPauses -= 1;
      else userPaused = true;
      if (video.paused) expectedPlaying = false;
      updateToggle();
    };
    const onPlay = () => {
      expectedPlaying = !video.paused;
      updateToggle();
    };
    const onToggle = () => {
      const request = ++manualPlaybackRequest;
      if (!video.paused) {
        userPaused = true;
        expectedPlaying = false;
        video.pause();
        updateToggle();
        return;
      }
      const oneOffPlayback = reducedMotion.matches || Boolean(connection?.saveData);
      const resumed = () => {
        if (destroyed || request !== manualPlaybackRequest) return;
        if (!video.paused) {
          userPaused = oneOffPlayback || reducedMotion.matches || Boolean(connection?.saveData);
          if (!userPaused) autoplayBlocked = false;
        }
        updateToggle();
      };
      const failed = () => {
        if (destroyed || request !== manualPlaybackRequest) return;
        autoplayBlocked = true;
        updateToggle();
      };
      try {
        const result = video.play();
        if (result?.then) result.then(resumed, failed);
        else resumed();
      } catch (error) {
        failed();
      }
      updateToggle();
    };
    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener('click', onToggle);
      updateToggle();
    }
    video.addEventListener('pause', onPause);
    video.addEventListener('play', onPlay);
    video.addEventListener('ended', updateToggle);
    document.addEventListener('visibilitychange', syncPlayback);
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', syncPlayback);
    else reducedMotion.addListener(syncPlayback);
    connection?.addEventListener?.('change', syncPlayback);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.15;
        syncPlayback();
      });
    }, { threshold: [0, 0.15] }) : null;
    observer?.observe(video);
    syncPlayback();
    films.set(video, () => {
      destroyed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', syncPlayback);
      else reducedMotion.removeListener(syncPlayback);
      connection?.removeEventListener?.('change', syncPlayback);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('ended', updateToggle);
      if (toggle) {
        toggle.removeEventListener('click', onToggle);
        toggle.hidden = true;
      }
      pauseForEnvironment();
      films.delete(video);
    });
  };
  const initialize = (root) => {
    root.querySelectorAll('[data-hm-r2-brand-film]').forEach(initializeFilm);
    root.querySelectorAll('[data-hm-r2-catalog]').forEach((catalog) => {
      if (catalog.dataset.hmR2Ready) return;
      catalog.dataset.hmR2Ready = 'true';
      const cards = [...catalog.querySelectorAll('[data-hm-r2-category]')];
      const buttons = [...catalog.querySelectorAll('[data-hm-r2-filter]')];
      const result = catalog.querySelector('[data-hm-r2-result-count]');
      const applyFilter = (filter) => {
        let count = 0;
        cards.forEach((card) => {
          const category = card.dataset.hmR2Category;
          const visible = filter === 'all' || category === filter ||
            (filter === 'kettle_refrigeration' && ['refrigerators', 'freezers', 'kettles'].includes(category));
          card.hidden = !visible;
          if (visible) count += 1;
        });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.hmR2Filter === filter)));
        if (result) {
          const template = count === 1 ? catalog.dataset.countSingularTemplate : catalog.dataset.countTemplate;
          result.textContent = template.replace('__count__', String(count));
        }
      };
      buttons.forEach((button) => button.addEventListener('click', () => applyFilter(button.dataset.hmR2Filter)));
      applyFilter(catalog.dataset.initialFilter || 'all');
    });
    root.querySelectorAll('[data-hm-r2-menu]').forEach((menu) => {
      if (menu.dataset.hmR2Ready) return;
      menu.dataset.hmR2Ready = 'true';
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => { menu.open = false; }));
      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        menu.open = false;
        menu.querySelector('summary').focus();
      });
    });
  };
  initialize(document);
  const closeMenus = () => {
    document.querySelectorAll('[data-hm-r2-menu][open]').forEach((menu) => { menu.open = false; });
  };
  window.addEventListener('pagehide', closeMenus);
  window.addEventListener('pageshow', closeMenus);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    films.forEach((cleanup, video) => {
      if (event.target.contains(video)) cleanup();
    });
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-hm-r2-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
})();
